/**
 * Grouted Pile Capacity Calculation Engine
 * Based on Abbs & Needham (OTC-4852, 1985) rock-grout bond model
 * With End Bearing support via Abbs & Needham and API RP 2GEO methods
 */

/**
 * Default FOS targets (can be customized)
 */
export const DEFAULT_FOS_TARGETS = {
  compOperating: 2.0,
  compStorm: 1.5,
  tensOperating: 1.5,
  tensStorm: 1.5
};

// Mutable FOS targets (will be updated by UI)
export let FOS_TARGETS = { ...DEFAULT_FOS_TARGETS };

/**
 * Update FOS targets
 * @param {Object} newTargets - New FOS target values
 */
export const updateFOSTargets = (newTargets) => {
  FOS_TARGETS = { ...FOS_TARGETS, ...newTargets };
};

/**
 * Reset FOS targets to defaults
 */
export const resetFOSTargets = () => {
  FOS_TARGETS = { ...DEFAULT_FOS_TARGETS };
};

/**
 * Calculate T_RG from q_c using Abbs & Needham correlation (OVERRIDE)
 * Rock-Grout Bond Correlation for carbonate rock
 * @param {number} qc - Unconfined compressive strength (MPa or MN/m2)
 * @returns {number} - Rock-grout bond strength T_RG (MPa or MN/m2)
 * 
 * Piecewise correlation:
 * - q_c < 1.0:        T_RG = 0.375 * q_c
 * - 1.0 <= q_c <= 3.0: T_RG = 0.375 + 0.1875 * (q_c - 1.0)
 * - q_c > 3.0:        T_RG = 0.75 (cap)
 */
export const calculateTrgFromQu = (qc) => {
  if (qc < 1.0) {
    return 0.375 * qc;
  } else if (qc <= 3.0) {
    return 0.375 + 0.1875 * (qc - 1.0);
  } else {
    return 0.75; // Cap at 0.75 MPa
  }
};

/**
 * Calculate steel-grout bond from grout strength or return manual value
 * T_sg_auto = 0.03 * f_c, capped at 1.5 MPa
 * @param {Object} pileInputs - Pile input parameters
 * @returns {number} - Steel-grout bond strength (MPa)
 */
export const getSteelGroutBond = (pileInputs) => {
  // Handle legacy data that might have old steelGroutBond field
  if (pileInputs.steelGroutBondMode === undefined) {
    return pileInputs.steelGroutBond || 0.2;
  }
  
  if (pileInputs.steelGroutBondMode === 'manual') {
    return pileInputs.steelGroutBondManual;
  }
  
  // Auto mode: T_sg = 0.03 * f_c, capped at 1.5 MPa
  const tAuto = 0.03 * pileInputs.groutStrength;
  return Math.min(tAuto, 1.5);
};

/**
 * Calculate auto steel-grout bond value (for display)
 * @param {number} groutStrength - Grout compressive strength (MPa)
 * @returns {number} - Auto-calculated steel-grout bond (MPa)
 */
export const calculateAutoSteelGroutBond = (groutStrength) => {
  const tAuto = 0.03 * groutStrength;
  return Math.min(tAuto, 1.5);
};

/**
 * Calculate steel area for a circular hollow section
 * @param {number} diameter - Outer diameter (mm)
 * @param {number} wallThickness - Wall thickness (mm)
 * @returns {number} - Steel area (mm²)
 */
export const calculateSteelArea = (diameter, wallThickness) => {
  const outerRadius = diameter / 2;
  const innerRadius = outerRadius - wallThickness;
  return Math.PI * (outerRadius ** 2 - innerRadius ** 2);
};

/**
 * Calculate end bearing capacity using Abbs & Needham method
 * Based on rock strength correlation
 * @param {number} qu - Average rock q_u at pile tip (MPa)
 * @param {number} diameter - Pile diameter (mm)
 * @returns {number} - End bearing capacity (MN)
 */
export const calculateEndBearingAbbsNeedham = (qu, diameter) => {
  // End bearing factor Nq for weak rock typically 3-6
  // Using conservative correlation: q_b = 2.5 * qu (capped)
  const qb = Math.min(2.5 * qu, 5.0); // MPa, capped at 5 MPa
  const area = Math.PI * Math.pow(diameter / 1000 / 2, 2); // m²
  return qb * area; // MN
};

/**
 * Calculate end bearing capacity using API RP 2GEO method
 * @param {Object} params - End bearing parameters
 * @returns {number} - End bearing capacity (MN)
 */
export const calculateEndBearingAPI = (params) => {
  const {
    soilType, // 'clay' | 'sand' | 'rock'
    su, // Undrained shear strength for clay (kPa)
    effectiveStress, // Effective vertical stress at pile tip (kPa)
    frictionAngle, // Soil friction angle for sand (degrees)
    qu, // Rock UCS for rock (MPa)
    diameter // Pile diameter (mm)
  } = params;

  const area = Math.PI * Math.pow(diameter / 1000 / 2, 2); // m²
  let qb = 0; // Unit end bearing (MPa)

  switch (soilType) {
    case 'clay':
      // q_b = 9 * s_u (API RP 2GEO)
      qb = (9 * su) / 1000; // Convert kPa to MPa
      break;
    case 'sand':
      // q_b = Nq * σ'v where Nq depends on friction angle
      const phi = frictionAngle * Math.PI / 180;
      const Nq = Math.exp(Math.PI * Math.tan(phi)) * Math.pow(Math.tan(Math.PI / 4 + phi / 2), 2);
      qb = (Nq * effectiveStress) / 1000; // Convert kPa to MPa
      qb = Math.min(qb, 10); // Cap at 10 MPa for sand
      break;
    case 'rock':
      // q_b = 2.5 * q_u for weak rock, capped
      qb = Math.min(2.5 * qu, 10); // MPa
      break;
    default:
      qb = 0;
  }

  return qb * area; // MN
};

/**
 * Main capacity calculation function
 * @param {Object} inputs - All input parameters
 * @returns {Object} - Calculation results
 */
export const calculateCapacity = (inputs) => {
  const {
    steelYieldStrength, // MPa
    groutLength, // m
    groutStrength, // MPa
    pileSections, // Array of {diameter, wallThickness, length}
    soilLayers, // Array of {from, to, qu, gamma, rqd, trg}
    holeOversize, // mm
    factorOfSafety = 2.5,
    // End bearing parameters
    endBearingEnabled = false,
    endBearingMethod = 'abbsNeedham', // 'abbsNeedham' | 'api'
    endBearingParams = {}
  } = inputs;

  // Get effective steel-grout bond (handles auto/manual mode)
  const steelGroutBond = getSteelGroutBond(inputs);
  const steelGroutBondMode = inputs.steelGroutBondMode || 'manual';

  // Calculate pile geometry
  const totalPileLength = pileSections.reduce((sum, s) => sum + s.length, 0);
  const embeddedLength = totalPileLength; // Assuming full pile is embedded
  
  // Calculate total steel area and find representative diameter
  let totalSteelArea = 0;
  let weightedDiameterSum = 0;
  let totalLength = 0;
  
  pileSections.forEach(section => {
    const area = calculateSteelArea(section.diameter, section.wallThickness);
    totalSteelArea += area;
    weightedDiameterSum += section.diameter * section.length;
    totalLength += section.length;
  });
  
  const avgDiameter = totalLength > 0 ? weightedDiameterSum / totalLength : 762; // mm
  const avgDiameterM = avgDiameter / 1000; // m
  
  // Steel capacity: Q_steel = A_s × f_y (convert mm² to m², MPa to kN/m², then to MN)
  const steelCapacityMN = (totalSteelArea * 1e-6) * (steelYieldStrength * 1000) / 1000;
  
  // Steel-grout bond capacity: Q_sg = π × D × L_grout × T_sg
  const steelGroutCapacityMN = Math.PI * avgDiameterM * groutLength * steelGroutBond;
  
  // Steel-grout connection capacity (governing)
  const steelGroutConnectionCapacity = Math.min(steelCapacityMN, steelGroutCapacityMN);
  
  // Calculate shaft capacity from soil layers
  const layerContributions = [];
  let shaftCapacity = 0;
  let tipQu = 0; // q_u at pile tip for end bearing
  
  soilLayers.forEach((layer, index) => {
    // Clip layer to embedded length
    const layerFrom = Math.max(0, Math.min(layer.from, embeddedLength));
    const layerTo = Math.max(0, Math.min(layer.to, embeddedLength));
    const layerThickness = layerTo - layerFrom;
    
    if (layerThickness > 0) {
      // Q_i = π × D × L_i × T_RG,i (in MN)
      const contribution = Math.PI * avgDiameterM * layerThickness * layer.trg;
      
      layerContributions.push({
        layerIndex: index + 1,
        from: layer.from,
        to: layer.to,
        effectiveFrom: layerFrom,
        effectiveTo: layerTo,
        thickness: layerThickness,
        qu: layer.qu,
        trg: layer.trg,
        gamma: layer.gamma,
        rqd: layer.rqd,
        contribution: contribution
      });
      
      shaftCapacity += contribution;
      
      // Track tip q_u (last layer at pile tip)
      if (layerTo >= embeddedLength - 0.1) {
        tipQu = layer.qu;
      }
    }
  });

  // Calculate end bearing capacity if enabled
  let endBearingCapacity = 0;
  let endBearingDetails = null;

  if (endBearingEnabled) {
    if (endBearingMethod === 'abbsNeedham') {
      endBearingCapacity = calculateEndBearingAbbsNeedham(tipQu, avgDiameter);
      endBearingDetails = {
        method: 'Abbs & Needham',
        tipQu: tipQu,
        qb: Math.min(2.5 * tipQu, 5.0),
        area: Math.PI * Math.pow(avgDiameter / 1000 / 2, 2)
      };
    } else if (endBearingMethod === 'api') {
      const apiParams = {
        soilType: endBearingParams.soilType || 'rock',
        su: endBearingParams.su || 100,
        effectiveStress: endBearingParams.effectiveStress || 200,
        frictionAngle: endBearingParams.frictionAngle || 30,
        qu: tipQu,
        diameter: avgDiameter
      };
      endBearingCapacity = calculateEndBearingAPI(apiParams);
      endBearingDetails = {
        method: 'API RP 2GEO',
        ...apiParams,
        area: Math.PI * Math.pow(avgDiameter / 1000 / 2, 2)
      };
    }
  }

  // Total shaft + end bearing
  const totalAxialCapacity = shaftCapacity + endBearingCapacity;
  
  // Ultimate capacity: Q_u = min(Q_shaft + Q_eb, Q_sg, Q_steel)
  const ultimateCapacity = Math.min(totalAxialCapacity, steelGroutConnectionCapacity);
  
  // Allowable capacity: Q_allow = Q_u / FS
  const allowableCapacity = ultimateCapacity / factorOfSafety;
  
  // Determine governing component
  let governingComponent = 'Shaft Resistance';
  if (endBearingEnabled && totalAxialCapacity < steelGroutConnectionCapacity) {
    governingComponent = 'Shaft + End Bearing';
  }
  if (steelCapacityMN <= totalAxialCapacity && steelCapacityMN <= steelGroutCapacityMN) {
    governingComponent = 'Steel Capacity';
  } else if (steelGroutCapacityMN <= totalAxialCapacity && steelGroutCapacityMN <= steelCapacityMN) {
    governingComponent = 'Steel-Grout Bond';
  }
  
  // Generate load-settlement curve data
  const loadSettlementData = generateLoadSettlementCurve(ultimateCapacity, avgDiameter, governingComponent);
  
  return {
    // Pile geometry
    totalPileLength,
    embeddedLength,
    totalSteelArea, // mm²
    avgDiameter, // mm
    
    // Steel calculations
    steelCapacityMN,
    steelGroutCapacityMN,
    steelGroutConnectionCapacity,
    
    // Shaft calculations
    shaftCapacity,
    layerContributions,
    
    // End bearing calculations
    endBearingEnabled,
    endBearingCapacity,
    endBearingMethod,
    endBearingDetails,
    
    // Final results
    ultimateCapacity,
    allowableCapacity,
    factorOfSafety,
    governingComponent,
    
    // Steel-grout bond info
    steelGroutBond,
    steelGroutBondMode,
    groutStrength,
    
    // Shaft percentage
    shaftPercentage: (shaftCapacity / Math.max(totalAxialCapacity, steelGroutConnectionCapacity)) * 100,
    
    // End bearing percentage
    endBearingPercentage: endBearingEnabled 
      ? (endBearingCapacity / Math.max(totalAxialCapacity, steelGroutConnectionCapacity)) * 100 
      : 0,
    
    // Load-settlement data
    loadSettlementData
  };
};

/**
 * Calculate Factor of Safety for design loads
 * @param {number} ultimateCapacity - Ultimate capacity (MN)
 * @param {Object} designLoads - Design loads object
 * @param {Object} customTargets - Custom FOS targets (optional)
 * @returns {Object} - FOS results
 */
export const calculateFOS = (ultimateCapacity, designLoads = {}, customTargets = null) => {
  const targets = customTargets || FOS_TARGETS;
  
  const {
    compOperating = 0,
    compStorm = 0,
    tensOperating = 0,
    tensStorm = 0,
    compOperatingCaseId = '',
    compStormCaseId = '',
    tensOperatingCaseId = '',
    tensStormCaseId = ''
  } = designLoads;

  const result = {
    compOperating: null,
    compStorm: null,
    tensOperating: null,
    tensStorm: null,
    compOperatingStatus: null,
    compStormStatus: null,
    tensOperatingStatus: null,
    tensStormStatus: null,
    minFS: null,
    governingCase: null,
    governingCaseId: null,
    targets: { ...targets }
  };

  const fsValues = [];

  // Operating compression
  if (compOperating > 0) {
    result.compOperating = ultimateCapacity / compOperating;
    result.compOperatingStatus = result.compOperating >= targets.compOperating ? 'PASS' : 'FAIL';
    fsValues.push({
      fs: result.compOperating,
      case: 'Operating compression',
      caseId: compOperatingCaseId
    });
  }

  // Storm compression
  if (compStorm > 0) {
    result.compStorm = ultimateCapacity / compStorm;
    result.compStormStatus = result.compStorm >= targets.compStorm ? 'PASS' : 'FAIL';
    fsValues.push({
      fs: result.compStorm,
      case: 'Storm compression',
      caseId: compStormCaseId
    });
  }

  // Operating tension
  if (tensOperating > 0) {
    result.tensOperating = ultimateCapacity / tensOperating;
    result.tensOperatingStatus = result.tensOperating >= targets.tensOperating ? 'PASS' : 'FAIL';
    fsValues.push({
      fs: result.tensOperating,
      case: 'Operating tension',
      caseId: tensOperatingCaseId
    });
  }

  // Storm tension
  if (tensStorm > 0) {
    result.tensStorm = ultimateCapacity / tensStorm;
    result.tensStormStatus = result.tensStorm >= targets.tensStorm ? 'PASS' : 'FAIL';
    fsValues.push({
      fs: result.tensStorm,
      case: 'Storm tension',
      caseId: tensStormCaseId
    });
  }

  // Find minimum FS and governing case
  if (fsValues.length > 0) {
    const minEntry = fsValues.reduce((min, curr) => curr.fs < min.fs ? curr : min, fsValues[0]);
    result.minFS = minEntry.fs;
    result.governingCase = minEntry.case;
    result.governingCaseId = minEntry.caseId;
  }

  return result;
};

/**
 * Get status based on minFS thresholds
 * @param {number} minFS - Minimum factor of safety
 * @returns {string} - 'PASS', 'WARNING', or 'FAIL'
 */
export const getFOSStatus = (minFS) => {
  if (minFS === null || minFS === undefined) return null;
  if (minFS >= 1.5) return 'PASS';
  if (minFS >= 1.2) return 'WARNING';
  return 'FAIL';
};

/**
 * Get color based on minFS thresholds
 * @param {number} minFS - Minimum factor of safety
 * @returns {string} - CSS color class
 */
export const getFOSColor = (minFS) => {
  if (minFS === null || minFS === undefined) return 'gray';
  if (minFS >= 1.5) return 'green';
  if (minFS >= 1.2) return 'amber';
  return 'red';
};

/**
 * Generate load-settlement curve data
 * @param {number} ultimateCapacity - Ultimate capacity (MN)
 * @param {number} diameter - Pile diameter (mm)
 * @param {string} governingComponent - Which component governs
 * @returns {Array} - Load-settlement data points
 */
const generateLoadSettlementCurve = (ultimateCapacity, diameter, governingComponent) => {
  const data = [];
  const maxSettlement = diameter * 0.1; // 10% of pile diameter
  const steps = 50;
  
  for (let i = 0; i <= steps; i++) {
    const settlement = (maxSettlement / steps) * i;
    const settlementPercent = (settlement / diameter) * 100;
    
    let load;
    const criticalSettlement = diameter * 0.02; // ~2% of diameter for ultimate
    
    if (settlement <= criticalSettlement) {
      // Linear elastic portion up to ultimate
      load = (settlement / criticalSettlement) * ultimateCapacity;
    } else {
      // Post-peak softening (20-30% reduction)
      const softeningFactor = 0.75 + 0.25 * Math.exp(-(settlement - criticalSettlement) / (diameter * 0.03));
      load = ultimateCapacity * softeningFactor;
    }
    
    const percentOfUltimate = (load / ultimateCapacity) * 100;
    
    data.push({
      settlement: Math.round(settlement * 100) / 100,
      settlementPercent: Math.round(settlementPercent * 100) / 100,
      load: Math.round(load * 1000) / 1000,
      percentOfUltimate: Math.round(percentOfUltimate * 100) / 100,
      governingComponent
    });
  }
  
  return data;
};

/**
 * Run sensitivity analysis
 * @param {Object} baseInputs - Base input parameters
 * @param {string} parameter - Parameter to vary
 * @param {number} fromValue - Start value (or multiplier)
 * @param {number} toValue - End value (or multiplier)
 * @param {number} steps - Number of steps
 * @param {boolean} useMultiplier - Whether values are multipliers
 * @returns {Array} - Sensitivity results
 */
export const runSensitivityAnalysis = (baseInputs, parameter, fromValue, toValue, steps, useMultiplier = true) => {
  const results = [];
  const stepSize = (toValue - fromValue) / (steps - 1);
  
  for (let i = 0; i < steps; i++) {
    const currentValue = fromValue + stepSize * i;
    const modifiedInputs = JSON.parse(JSON.stringify(baseInputs));
    let parameterValue;
    
    switch (parameter) {
      case 'avgQu':
        // Scale all layer q_u values
        modifiedInputs.soilLayers = modifiedInputs.soilLayers.map(layer => ({
          ...layer,
          qu: useMultiplier ? layer.qu * currentValue : layer.qu * (currentValue / getAvgQu(baseInputs.soilLayers))
        }));
        parameterValue = useMultiplier ? currentValue : currentValue;
        break;
        
      case 'pileDiameter':
        // Scale pile diameter
        modifiedInputs.pileSections = modifiedInputs.pileSections.map(section => ({
          ...section,
          diameter: useMultiplier ? section.diameter * currentValue : currentValue
        }));
        parameterValue = useMultiplier 
          ? baseInputs.pileSections[0]?.diameter * currentValue 
          : currentValue;
        break;
        
      case 'groutLength':
        modifiedInputs.groutLength = useMultiplier 
          ? baseInputs.groutLength * currentValue 
          : currentValue;
        parameterValue = modifiedInputs.groutLength;
        break;
        
      case 'groutStrength':
        modifiedInputs.groutStrength = useMultiplier 
          ? baseInputs.groutStrength * currentValue 
          : currentValue;
        parameterValue = modifiedInputs.groutStrength;
        break;
        
      case 'avgTrg':
        // Scale all layer T_RG values
        modifiedInputs.soilLayers = modifiedInputs.soilLayers.map(layer => ({
          ...layer,
          trg: useMultiplier ? layer.trg * currentValue : layer.trg * (currentValue / getAvgTrg(baseInputs.soilLayers))
        }));
        parameterValue = useMultiplier ? currentValue : currentValue;
        break;
        
      default:
        parameterValue = currentValue;
    }
    
    const result = calculateCapacity(modifiedInputs);
    
    results.push({
      step: i + 1,
      multiplier: currentValue,
      parameterValue: Math.round(parameterValue * 1000) / 1000,
      ultimateCapacity: Math.round(result.ultimateCapacity * 1000) / 1000,
      allowableCapacity: Math.round(result.allowableCapacity * 1000) / 1000,
      shaftCapacity: Math.round(result.shaftCapacity * 1000) / 1000,
      governingComponent: result.governingComponent
    });
  }
  
  return results;
};

/**
 * Get average q_u from soil layers
 */
const getAvgQu = (soilLayers) => {
  if (!soilLayers || soilLayers.length === 0) return 1;
  let totalThickness = 0;
  let weightedSum = 0;
  soilLayers.forEach(layer => {
    const thickness = layer.to - layer.from;
    weightedSum += layer.qu * thickness;
    totalThickness += thickness;
  });
  return totalThickness > 0 ? weightedSum / totalThickness : 1;
};

/**
 * Get average T_RG from soil layers
 */
const getAvgTrg = (soilLayers) => {
  if (!soilLayers || soilLayers.length === 0) return 1;
  let totalThickness = 0;
  let weightedSum = 0;
  soilLayers.forEach(layer => {
    const thickness = layer.to - layer.from;
    weightedSum += layer.trg * thickness;
    totalThickness += thickness;
  });
  return totalThickness > 0 ? weightedSum / totalThickness : 1;
};

/**
 * Export results to CSV
 * @param {Object} results - Calculation results
 * @returns {string} - CSV string
 */
export const exportToCSV = (results) => {
  let csv = 'Grouted Pile Capacity Results\n\n';
  
  // Summary
  csv += 'Summary\n';
  csv += `Ultimate Capacity (MN),${results.ultimateCapacity.toFixed(3)}\n`;
  csv += `Allowable Capacity (MN),${results.allowableCapacity.toFixed(3)}\n`;
  csv += `Factor of Safety,${results.factorOfSafety}\n`;
  csv += `Governing Component,${results.governingComponent}\n`;
  
  if (results.endBearingEnabled) {
    csv += `End Bearing Capacity (MN),${results.endBearingCapacity.toFixed(3)}\n`;
    csv += `End Bearing Method,${results.endBearingMethod}\n`;
  }
  csv += '\n';
  
  // Layer contributions
  csv += 'Layer Contributions\n';
  csv += 'Layer,From (m),To (m),qu (MPa),T_RG (MPa),Contribution (MN)\n';
  csv += `Steel-Grout,--,--,--,--,${results.steelGroutConnectionCapacity.toFixed(3)}\n`;
  
  results.layerContributions.forEach(layer => {
    csv += `${layer.layerIndex},${layer.from},${layer.to},${layer.qu},${layer.trg},${layer.contribution.toFixed(3)}\n`;
  });
  
  if (results.endBearingEnabled) {
    csv += `End Bearing,--,--,--,--,${results.endBearingCapacity.toFixed(3)}\n`;
  }
  
  csv += `Total,--,--,--,--,${results.ultimateCapacity.toFixed(3)}\n\n`;
  
  // Load-settlement data
  csv += 'Load-Settlement Data\n';
  csv += 'Settlement (mm),Settlement (%),Load (MN),% of Ultimate\n';
  
  results.loadSettlementData.forEach(point => {
    csv += `${point.settlement},${point.settlementPercent},${point.load},${point.percentOfUltimate}\n`;
  });
  
  return csv;
};

/**
 * Download string as file
 * @param {string} content - File content
 * @param {string} filename - File name
 * @param {string} mimeType - MIME type
 */
export const downloadFile = (content, filename, mimeType = 'text/csv') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
